import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  RefreshCw,
  Building2,
  Clock,
  ArrowUpDown,
  TrendingUp,
  Flame,
  X,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface SeedFailureRow {
  workspaceId: string;
  workspaceName: string | null;
  seedKey: string;
  error: string;
  firstSeenAt: string;
  lastAttemptAt: string;
}

interface SeedFailureHistoryStats {
  newLast7Days: number;
  newLast30Days: number;
  topSeedKeys: Array<{ seedKey: string; count: number }>;
}

interface SeedFailuresResponse {
  failures: SeedFailureRow[];
  // Optional so the page degrades gracefully if an older server build
  // (pre-#210) responds without history aggregation. When absent we
  // fall back to deriving the trend cards client-side from the live
  // `failures` payload — which only sees currently-broken seeds and
  // therefore undercounts churn, but is still better than nothing.
  historyStats?: SeedFailureHistoryStats;
}

interface RetryResponse {
  workspaceId: string;
  workspaceName: string | null;
  inserted: number;
  backfilled: number;
  failures: { seedKey: string; error: string }[];
}

type SortKey = "firstSeenAt" | "lastAttemptAt" | "workspaceName" | "seedKey";
type SortDir = "asc" | "desc";

export interface SeedFailureTrendStats {
  total: number;
  newLast7Days: number;
  newLast30Days: number;
  topSeedKeys: Array<{ seedKey: string; count: number }>;
}

/**
 * Pure derivation of the trend-stat summary shown above the failures
 * table. Exported so the unit test can exercise the seven-vs-thirty
 * day windowing and the top-3 tie-breaking without mounting the page
 * (which would otherwise drag in PlatformLayout + auth queries).
 *
 * @param rows  The same payload the table renders.
 * @param nowMs Override the wall-clock for deterministic tests; falls
 *              back to `Date.now()` in production.
 */
export function computeSeedFailureTrendStats(
  rows: ReadonlyArray<Pick<SeedFailureRow, "seedKey" | "firstSeenAt">>,
  nowMs: number = Date.now(),
): SeedFailureTrendStats {
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

  let newLast7Days = 0;
  let newLast30Days = 0;
  const seedKeyCounts = new Map<string, number>();
  for (const row of rows) {
    const firstSeenMs = new Date(row.firstSeenAt).getTime();
    if (!Number.isNaN(firstSeenMs)) {
      const age = nowMs - firstSeenMs;
      // `age >= 0` keeps clock-skewed / future-dated rows out of the
      // "new in last N days" buckets — they still count toward `total`
      // and the seed-key tally, but they shouldn't inflate trend
      // metrics on the basis of a bad timestamp.
      if (age >= 0 && age <= sevenDaysMs) newLast7Days += 1;
      if (age >= 0 && age <= thirtyDaysMs) newLast30Days += 1;
    }
    seedKeyCounts.set(
      row.seedKey,
      (seedKeyCounts.get(row.seedKey) ?? 0) + 1,
    );
  }

  // Sort by count desc, then seedKey asc for a deterministic ordering
  // when several keys are tied (e.g. all-1s on a healthy platform).
  const topSeedKeys = Array.from(seedKeyCounts.entries())
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
    })
    .slice(0, 3)
    .map(([seedKey, count]) => ({ seedKey, count }));

  return {
    total: rows.length,
    newLast7Days,
    newLast30Days,
    topSeedKeys,
  };
}

function formatRelativeAge(iso: string): string {
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month}mo ago`;
  const year = Math.floor(day / 365);
  return `${year}y ago`;
}

export function SeedFailuresContent() {
  const { toast } = useToast();
  const [sortKey, setSortKey] = useState<SortKey>("firstSeenAt");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  // When set, only failure rows whose `seedKey` matches are shown in
  // the table below. Driven by clicking an entry in the
  // "Top Failing Seed Keys" card so admins can jump from "see the
  // regression" to "act on it" without scrolling. Session-scoped only —
  // we intentionally don't persist across reloads (per task #211).
  const [seedKeyFilter, setSeedKeyFilter] = useState<string | null>(null);

  const { data, isLoading } = useQuery<SeedFailuresResponse>({
    queryKey: ["/api/platform/seed-failures"],
  });

  const retryMutation = useMutation({
    mutationFn: async (workspaceId: string): Promise<RetryResponse> => {
      const res = await apiRequest("POST", "/api/platform/seed-failures/retry", {
        workspaceId,
      });
      return (await res.json()) as RetryResponse;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/seed-failures"] });
      const wsLabel = result.workspaceName || result.workspaceId;
      if (result.failures.length > 0) {
        toast({
          title: `Retry still failing for ${wsLabel}`,
          description: `${result.failures.length} seed key(s) still erroring. First: ${result.failures[0].error.slice(0, 120)}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: `Retry succeeded for ${wsLabel}`,
          description: `Inserted ${result.inserted}, backfilled ${result.backfilled}.`,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Retry failed",
        description: error?.message || "Could not retry seeds for that workspace.",
        variant: "destructive",
      });
    },
  });

  const sortedRows = useMemo(() => {
    const rows = data?.failures ?? [];
    // Apply the seed-key filter (if any) before sorting so the table
    // only shows the rows the admin asked to focus on. Filter is a
    // strict equality check against the same `seedKey` rendered in the
    // top-keys card, so what the admin clicked is exactly what they get.
    const filtered = seedKeyFilter
      ? rows.filter((r) => r.seedKey === seedKeyFilter)
      : rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [data?.failures, sortKey, sortDir, seedKeyFilter]);

  // Prefer server-side history-derived trend stats (task #210) so the
  // 7/30-day counts include already-resolved incidents — the live
  // `failures` payload only sees currently-broken seeds and therefore
  // silently undercounts churn. Fall back to the in-memory reducer if
  // an older server build responds without `historyStats`.
  const fallbackTrendStats = useMemo(
    () => computeSeedFailureTrendStats(data?.failures ?? []),
    [data?.failures],
  );
  const trendStats: SeedFailureTrendStats = data?.historyStats
    ? {
        // `total` continues to mean "active failures right now" — that
        // metric reads from the live table, not the history.
        total: data?.failures?.length ?? 0,
        newLast7Days: data.historyStats.newLast7Days,
        newLast30Days: data.historyStats.newLast30Days,
        topSeedKeys: data.historyStats.topSeedKeys,
      }
    : fallbackTrendStats;
  // Whether the top-keys card is reading from history (event count over
  // the 30-day window) vs. the live failures list (workspace count for
  // currently-broken seeds). Drives the per-row suffix wording.
  const topKeysFromHistory = Boolean(data?.historyStats);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Age-style columns default to ascending (oldest-first); names
      // default to ascending alphabetical.
      setSortDir("asc");
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <>
      <div className="p-3 sm:p-6 space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Seed Failures</h1>
          <p className="text-muted-foreground">
            Cross-workspace view of preset master templates that failed to seed.
            Sorted by age so the longest-broken seeds surface first.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"
              data-testid="loading-seed-failures"
            />
          </div>
        ) : (
          <>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4"
              data-testid="section-trend-stats"
            >
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Active Failures
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="text-2xl font-bold"
                    data-testid="stat-total-active"
                  >
                    {trendStats.total}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Across all workspaces right now.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <TrendingUp className="h-4 w-4 text-rose-500" />
                    Failures in Last 7 Days
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className="text-2xl font-bold"
                    data-testid="stat-new-7-days"
                  >
                    {trendStats.newLast7Days}
                  </div>
                  <p
                    className="text-xs text-muted-foreground mt-1"
                    data-testid="stat-new-30-days"
                  >
                    {trendStats.newLast30Days} in last 30 days
                    {topKeysFromHistory
                      ? " (includes resolved)."
                      : "."}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Flame className="h-4 w-4 text-orange-500" />
                    Top Failing Seed Keys
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {trendStats.topSeedKeys.length === 0 ? (
                    <p
                      className="text-sm text-muted-foreground"
                      data-testid="text-top-seedkeys-empty"
                    >
                      None — platform is healthy.
                    </p>
                  ) : (
                    <ul
                      className="space-y-1"
                      data-testid="list-top-seedkeys"
                    >
                      {trendStats.topSeedKeys.map((entry) => {
                        const isActive = seedKeyFilter === entry.seedKey;
                        return (
                          <li
                            key={entry.seedKey}
                            data-testid={`row-top-seedkey-${entry.seedKey}`}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setSeedKeyFilter(
                                  isActive ? null : entry.seedKey,
                                )
                              }
                              aria-pressed={isActive}
                              title={
                                isActive
                                  ? `Clear filter on ${entry.seedKey}`
                                  : `Filter table to ${entry.seedKey}`
                              }
                              className={`w-full flex items-center justify-between gap-2 text-sm rounded px-2 py-1 -mx-2 hover-elevate active-elevate-2 ${
                                isActive ? "bg-muted" : ""
                              }`}
                              data-testid={`button-filter-seedkey-${entry.seedKey}`}
                            >
                              <Badge
                                variant={isActive ? "default" : "outline"}
                                className="font-mono text-xs truncate max-w-[200px]"
                                title={entry.seedKey}
                              >
                                {entry.seedKey}
                              </Badge>
                              <span
                                className="font-semibold tabular-nums text-muted-foreground"
                                data-testid={`count-top-seedkey-${entry.seedKey}`}
                              >
                                {entry.count}{" "}
                                {topKeysFromHistory
                                  ? `failure${entry.count === 1 ? "" : "s"} in 30d`
                                  : `workspace${entry.count === 1 ? "" : "s"}`}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Currently Failing Seeds
              </CardTitle>
              <CardDescription>
                {seedKeyFilter
                  ? sortedRows.length === 0
                    ? `No active failures for seed key "${seedKeyFilter}".`
                    : `${sortedRows.length} workspace${sortedRows.length === 1 ? "" : "s"} failing on "${seedKeyFilter}".`
                  : sortedRows.length === 0
                    ? "No active seed failures across the platform."
                    : `${sortedRows.length} active failure${sortedRows.length === 1 ? "" : "s"} across workspaces.`}
              </CardDescription>
              {seedKeyFilter && (
                <div
                  className="flex items-center gap-2 pt-2"
                  data-testid="chip-active-seedkey-filter"
                >
                  <span className="text-xs text-muted-foreground">
                    Filtered by seed key:
                  </span>
                  <Badge
                    variant="secondary"
                    className="font-mono text-xs gap-1 pr-1"
                  >
                    <span data-testid="text-active-seedkey-filter">
                      {seedKeyFilter}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSeedKeyFilter(null)}
                      aria-label={`Clear filter on ${seedKeyFilter}`}
                      title="Clear filter"
                      className="ml-1 rounded hover-elevate active-elevate-2 p-0.5"
                      data-testid="button-clear-seedkey-filter"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {sortedRows.length === 0 ? (
                <p
                  className="text-center text-muted-foreground py-8"
                  data-testid="text-empty-state"
                >
                  {seedKeyFilter
                    ? `No workspaces are currently failing on "${seedKeyFilter}". Clear the filter to see all failures.`
                    : "All workspaces are healthy — no preset templates currently failing."}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover-elevate active-elevate-2 px-2 py-1 rounded -mx-2"
                            onClick={() => toggleSort("workspaceName")}
                            data-testid="button-sort-workspace"
                          >
                            <Building2 className="h-3 w-3" />
                            Workspace{sortIndicator("workspaceName")}
                            <ArrowUpDown className="h-3 w-3 opacity-50" />
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover-elevate active-elevate-2 px-2 py-1 rounded -mx-2"
                            onClick={() => toggleSort("seedKey")}
                            data-testid="button-sort-seedkey"
                          >
                            Seed Key{sortIndicator("seedKey")}
                            <ArrowUpDown className="h-3 w-3 opacity-50" />
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover-elevate active-elevate-2 px-2 py-1 rounded -mx-2"
                            onClick={() => toggleSort("firstSeenAt")}
                            data-testid="button-sort-firstseen"
                          >
                            <Clock className="h-3 w-3" />
                            Broken Since{sortIndicator("firstSeenAt")}
                            <ArrowUpDown className="h-3 w-3 opacity-50" />
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 hover-elevate active-elevate-2 px-2 py-1 rounded -mx-2"
                            onClick={() => toggleSort("lastAttemptAt")}
                            data-testid="button-sort-lastattempt"
                          >
                            Last Attempt{sortIndicator("lastAttemptAt")}
                            <ArrowUpDown className="h-3 w-3 opacity-50" />
                          </button>
                        </TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedRows.map((row) => {
                        const rowKey = `${row.workspaceId}:${row.seedKey}`;
                        const isRetrying =
                          retryMutation.isPending &&
                          retryMutation.variables === row.workspaceId;
                        return (
                          <TableRow
                            key={rowKey}
                            data-testid={`row-seed-failure-${rowKey}`}
                          >
                            <TableCell>
                              <div
                                className="font-medium"
                                data-testid={`text-workspace-name-${rowKey}`}
                              >
                                {row.workspaceName || (
                                  <span className="italic text-muted-foreground">
                                    (unnamed)
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {row.workspaceId}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">
                                {row.seedKey}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div
                                className="text-sm"
                                data-testid={`text-firstseen-${rowKey}`}
                              >
                                {formatRelativeAge(row.firstSeenAt)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(row.firstSeenAt).toLocaleString()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div
                                className="text-sm"
                                data-testid={`text-lastattempt-${rowKey}`}
                              >
                                {formatRelativeAge(row.lastAttemptAt)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(row.lastAttemptAt).toLocaleString()}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-md">
                              <div
                                className="text-xs text-muted-foreground bg-slate-100 dark:bg-slate-800 rounded p-2 whitespace-pre-wrap break-words"
                                data-testid={`text-error-${rowKey}`}
                                title={row.error}
                              >
                                {row.error.slice(0, 280)}
                                {row.error.length > 280 ? "…" : ""}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={retryMutation.isPending}
                                onClick={() =>
                                  retryMutation.mutate(row.workspaceId)
                                }
                                data-testid={`button-retry-${rowKey}`}
                              >
                                <RefreshCw
                                  className={`h-3 w-3 mr-1 ${isRetrying ? "animate-spin" : ""}`}
                                />
                                Retry
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
          </>
        )}
      </div>
    </>
  );
}

export default function PlatformSeedFailures() {
  return <PlatformLayout><SeedFailuresContent /></PlatformLayout>;
}
